import {
    Component,
    OnInit,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
    HostListener,
    ElementRef
} from '@angular/core';
import { EventName, Events } from '../../../utils/events';
import { AppNavProvider } from '../../../providers/app_nav';
import { UserProvider } from '../../../providers/user';
import { Dataset, DatasetProvider } from '../../../providers/dataset';
import { NavSidebarProvider, SidebarClass, OutOfSyncState } from '../../../providers/nav-sidebar';
import { DatasetNavBarProvider, DatasetNavLocation } from '../../../providers/dataset_navbar';
import { CanvasProvider } from '../../../providers/workspace/canvas';
import { TaggingTool } from '../../../providers/workspace/tagging-tool.enum';
import { LocalStorageProvider } from '../../../providers/local-storage';
import { AnomalyWorkflowProvider } from '../../../providers/anomaly_workflow';
import { ActiveToolProvider } from '../../../providers/workspace/active-tool';
import { BoostProvider } from '../../../providers/boost';
import { Project, ProjectProvider } from '../../../providers/project';
import { ModalProvider } from '../../../providers/popup/modal';
import { SettingsProvider, SettingLifetime } from '../../../providers/settings';
import { Subscription } from 'rxjs';
import { AccountSettingsViews } from '../settings-sidebar/settings-sidebar';
import { JobProvider, JobResponse, JobsEventData, JobState, JobType } from '../../../providers/job';
import { SidebarJobProvider } from '../../../providers/sidebar-job';
import { TrackableJobProvider } from '../../../providers/trackable-job';
import { TranslateService } from '@ngx-translate/core';

// TODO: For BB-4325, investigate creating a 'breadcrumb provider'.
export interface Breadcrumb {
    title: string;
    link?: string;
    data?: any;
}

const MAX_JOBS_TO_DISPLAY = 3;

@Component({
    selector: 'nav-sidebar',
    templateUrl: 'nav-sidebar.html'
})
export class NavSidebarComponent implements OnInit, OnChanges, OnDestroy {
    public hasNavbar: boolean;
    public isExpanded: boolean;
    public isSettingsPage: boolean;

    public projects: Project[];
    public projectsSubscription: Subscription;
    public datasets: Dataset[];
    public datasetIdentifier: string | undefined;
    public projectIdentifier: string | undefined;
    public metadataSubscription: Subscription;
    public sidebarJobs: JobResponse[] = [];

    // Derived from the dataset/project IDs
    private dataset: Dataset | undefined;
    private project: Project | undefined;

    private hasBrainCapability = false;
    private projectListVisible = false;
    private datasetListVisible = false;
    @ViewChild('projectListButton') private projectListButton;
    @ViewChild('datasetListButton') private datasetListButton;
    public projectListStyles = {};
    public datasetListStyles = {};

    public OutOfSyncState = OutOfSyncState;

    @ViewChild('jobsScroller') private jobsScroller: ElementRef;
    @ViewChild('projectDropdown') private projectDropdown: ElementRef;
    @ViewChild('projectList') private projectList: ElementRef;
    @ViewChild('datasetDropdown') private datasetDropdown: ElementRef;
    @ViewChild('datasetList') private datasetList: ElementRef;
    @ViewChild('projectSearch') private projectSearch: ElementRef;
    @ViewChild('datasetSearch') private datasetSearch: ElementRef;

    public projectSearchQuery = '';
    public datasetSearchQuery = '';
    public filteredProjects: Project[] = [];
    public filteredDatasets: Dataset[] = [];

    public projectListHasScrolling = false;
    public datasetListHasScrolling = false;

    constructor(
        private appNavProvider: AppNavProvider,
        private events: Events,
        public userProvider: UserProvider,
        public navSidebarProvider: NavSidebarProvider,
        public datasetProvider: DatasetProvider,
        public datasetNavProvider: DatasetNavBarProvider,
        public canvasProvider: CanvasProvider,
        public activeToolProvider: ActiveToolProvider,
        public anomalyWorkflowProvider: AnomalyWorkflowProvider,
        public jobProvider: JobProvider,
        public boostProvider: BoostProvider,
        public projectProvider: ProjectProvider,
        public modalProvider: ModalProvider,
        public settingsProvider: SettingsProvider,
        public localStorageProvider: LocalStorageProvider,
        public sidebarJobProvider: SidebarJobProvider,
        private trackableJobProvider: TrackableJobProvider,
        private translate: TranslateService
    ) {}

    // To close the menus, we must check for an outside click.
    // We also clear the search query upon outside click.
    @HostListener('document:click', ['$event.target']) onClick(target: HTMLElement) {
        if (this.projectDropdown && !this.projectDropdown.nativeElement.contains(target)) {
            this.projectListVisible = false;
            this.projectSearchQuery = '';
            this.filteredProjects = this.projects;
        }

        if (this.datasetDropdown && !this.datasetDropdown.nativeElement.contains(target)) {
            this.datasetListVisible = false;
            this.datasetSearchQuery = '';
            this.filteredDatasets = this.datasets;
        }
    }

    @HostListener('window:popstate')
    onPopState() {
        this.projectListVisible = false;
        this.datasetListVisible = false;
    }

    async ngOnInit() {
        await this.settingsProvider.load();
        await this.datasetProvider.load();
        await this.projectProvider.load();

        this.isExpanded = this.settingsProvider.get('navSidebar.isExpanded');

        if (await this.showEnvWarning()) {
            this.localStorageProvider.set('warningShownThisSession', 'true');
            this.modalProvider
                .createModal('SuboptimalEnvWarningModal', {}, { cssClass: this.getClientEnvWarningClass() })
                .present();
        }

        this.projectsSubscription = this.projectProvider.projectsSubscription.subscribe(projects => {
            const sortedProjects = projects.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });

            // Dereference the projects subscription data to prevent a bug with the dropdown during
            // navigation between various projects.
            this.projects = [...sortedProjects];

            this.filteredProjects = this.projects;
        });

        this.metadataSubscription = this.navSidebarProvider.currentMetadataSubscription.subscribe(metadata => {
            if (!metadata) {
                return;
            }

            this.hasNavbar = metadata.hasNavbar;
            this.isSettingsPage = metadata.isSettingsPage;
            this.projectIdentifier = metadata.projectIdentifier;
            this.datasetIdentifier = metadata.datasetIdentifier;

            if (this.datasetIdentifier) {
                this.dataset = this.datasetProvider.getDatasetById(this.datasetIdentifier);
                this.hasBrainCapability = this.datasetProvider.hasBrainCapability(this.datasetIdentifier);
            } else {
                this.dataset = undefined;
                this.hasBrainCapability = false;
            }

            if (this.projectIdentifier) {
                this.project = this.projectProvider.getProjectById(this.projectIdentifier);
            } else {
                this.project = undefined;
            }

            this.datasets = this.getDatasets();
            this.filteredDatasets = this.datasets;
        });

        if (this.projectIdentifier) {
            this.project = this.projectProvider.getProjectById(this.projectIdentifier);
        }

        if (this.datasetIdentifier) {
            this.hasBrainCapability = this.datasetProvider.hasBrainCapability(this.datasetIdentifier);
        }

        this.events.subscribe(EventName.DatasetReloaded, this.onDatasetReloaded);
        this.events.subscribe(EventName.TrackableJobFinished, this.onTrackableJobFinished);
        this.events.subscribe(EventName.TrackableJobCanceled, this.onTrackableJobCanceled);
        this.events.subscribe(EventName.TrainFinished, this.onTrainFinished);
        this.events.subscribe(EventName.TrackableJobsPolled, this.onTrackableJobsPolled);
        this.events.subscribe(EventName.JobDismissed, this.onJobDismissed);
        this.events.subscribe(EventName.UploadDone, this.onUploadDone);

        if (this.dataset && this.dataset.brain) {
            await this.trackableJobProvider.loadJobStates(this.dataset.id);
        }

        const contentClass = this.isExpanded ? SidebarClass.EXPANDED : SidebarClass.COLLAPSED;
        this.navSidebarProvider.setContentClass(contentClass);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            changes.projectIdentifier &&
            changes.projectIdentifier.currentValue !== changes.projectIdentifier.previousValue
        ) {
            if (this.projectIdentifier) {
                this.project = this.projectProvider.getProjectById(this.projectIdentifier);
            } else {
                this.project = undefined;
            }
        }
    }

    ngOnDestroy() {
        this.events.unsubscribe(EventName.DatasetReloaded, this.onDatasetReloaded);
        this.events.unsubscribe(EventName.TrackableJobFinished, this.onTrackableJobFinished);
        this.events.unsubscribe(EventName.TrackableJobCanceled, this.onTrackableJobCanceled);
        this.events.unsubscribe(EventName.TrainFinished, this.onTrainFinished);
        this.events.unsubscribe(EventName.TrackableJobsPolled, this.onTrackableJobsPolled);
        this.events.unsubscribe(EventName.JobDismissed, this.onJobDismissed);
        this.events.unsubscribe(EventName.UploadDone, this.onUploadDone);

        this.projectsSubscription.unsubscribe();
        this.metadataSubscription.unsubscribe();
    }

    private onTrackableJobFinished = async () => {
        await this.reloadDataset();
    };

    private onTrackableJobCanceled = async () => {
        await this.reloadDataset();
    };

    private onTrainFinished = async () => {
        await this.reloadDataset();
    };

    private onTrackableJobsPolled = (jobsEventData: JobsEventData) => {
        this.updateJobSidebar(jobsEventData);
    };

    private onJobDismissed = (jobsEventData: JobsEventData) => {
        this.updateJobSidebar(jobsEventData);
    };

    private onDatasetReloaded = () => {
        setTimeout(() => {
            if (this.datasetIdentifier && this.projectIdentifier) {
                this.dataset = this.datasetProvider.getDatasetById(this.datasetIdentifier);
                this.datasets = this.getDatasets();
                this.project = this.projectProvider.getProjectById(this.projectIdentifier);
            }
        }, 0);
    };

    private onUploadDone = async () => {
        // Reloading the dataset will cause the dataset:reloaded event to be emitted
        // which we handed above in the onDatasetReloaded handler
        if (this.datasetIdentifier) {
            await this.datasetProvider.reloadDataset(this.datasetIdentifier);
        }
    };

    private async reloadDataset(): Promise<void> {
        if (this.datasetIdentifier && this.projectIdentifier) {
            this.dataset = await this.datasetProvider.reloadDataset(this.datasetIdentifier);
            this.datasets = this.getDatasets();
            this.project = this.projectProvider.getProjectById(this.projectIdentifier);
        }
    }

    private updateJobSidebar(jobsEventData: JobsEventData): void {
        const oldSidebarJobs = this.sidebarJobs;

        this.sidebarJobs = this.getSidebarJobs(jobsEventData.jobs);

        if (
            this.sidebarJobs.length > MAX_JOBS_TO_DISPLAY &&
            this.hasNewJob(oldSidebarJobs, this.sidebarJobs) &&
            this.jobsScroller
        ) {
            this.jobsScroller.nativeElement.scrollTop = this.jobsScroller.nativeElement.scrollHeight;
        }
    }

    public async showEnvWarning(): Promise<boolean> {
        try {
            const hideEnvWarning = this.settingsProvider.get('banner.envWarning.hide');
            const warningShownThisSession = await this.localStorageProvider.get('warningShownThisSession');

            if (hideEnvWarning || warningShownThisSession === 'true') {
                return false;
            }

            if (navigator.userAgent.toLowerCase().indexOf('mobile') != -1) {
                return true;
            }

            return (
                navigator.vendor != 'Google Inc.' ||
                (window.screen.availHeight < 800 && window.screen.availWidth < 1280)
            );
        } catch (e) {
            throw e;
        }
    }

    public getClientEnvWarningClass(): string {
        const screenInfo: Screen = window.screen;

        if (screenInfo.availHeight <= 800 || screenInfo.availWidth <= 1280) {
            if (screenInfo.availHeight < screenInfo.availWidth || screenInfo.availWidth >= 768) {
                return 'modal-suboptimal-env-phone-landscape';
            }
            return 'modal-suboptimal-env-phone-portrait';
        }

        return 'modal-suboptimal-env-computer';
    }

    public toggleExpanded(event): void {
        event.stopPropagation();
        event.preventDefault();

        this.isExpanded = !this.isExpanded;
        const contentClass = this.isExpanded ? SidebarClass.EXPANDED : SidebarClass.COLLAPSED;
        this.navSidebarProvider.setContentClass(contentClass);
        this.settingsProvider.set('navSidebar.isExpanded', this.isExpanded, SettingLifetime.Session);

        // Hide the project/dataset dropdowns when collapsing the menu.
        if (!this.isExpanded) {
            this.projectListVisible = false;
            this.datasetListVisible = false;
        }
    }

    /**
     * Breadcrumb styles & links
     * NOTE: These previously lived in the dataset navbar component!
     * We are only porting over the functions relevant to the left side nav-buttons in BB-4235!
     * The rest will be implemented in future features.
     */

    public canViewBrainPages(): boolean {
        return this.hasBrainCapability;
    }

    public isDatasetDashboard(): boolean {
        return this.datasetNavProvider.getActivePage() === DatasetNavLocation.DatasetDashboard;
    }

    public goToDatasetDashboard(): void {
        if (!this.isDatasetDashboard()) {
            this.appNavProvider.getNavController().setRoot('DatasetPage', {
                datasetIdentifier: this.datasetIdentifier
            });
        }
    }

    public isWorkspace(): boolean {
        return this.datasetNavProvider.getActivePage() === DatasetNavLocation.Workspace;
    }

    public canEnterWorkspace(): boolean {
        return (
            this.dataset !== undefined &&
            this.dataset.imageCounts !== undefined &&
            this.dataset.imageCounts.total !== undefined &&
            this.dataset.imageCounts.total > 0
        );
    }

    public workspaceNavText(): string {
        return this.hasBrainCapability
            ? this.translate.instant('Train Brain')
            : this.translate.instant('Annotate Dataset');
    }

    public goToWorkspace(taggingTool: TaggingTool | null = null): void {
        if (!this.isWorkspace()) {
            this.appNavProvider.getNavController().setRoot('WorkspacePage', {
                datasetIdentifier: this.datasetIdentifier,
                taggingTool
            });
        } else if (taggingTool) {
            this.activeToolProvider.setActiveTool(taggingTool);
        }
    }

    public isOnDetectionTrainingPage(): boolean {
        return this.datasetNavProvider.getActivePage() === DatasetNavLocation.DetectionTraining;
    }

    public canTrainDetectionBrain(): boolean {
        if (!this.dataset) {
            return false;
        }

        return this.datasetProvider.canTrainDetectionBrain(this.dataset.id);
    }

    public goToDetectionBrain(): void {
        if (this.canTrainDetectionBrain()) {
            this.appNavProvider.getNavController().setRoot(`DetectionTrainingPage`, {
                datasetIdentifier: this.datasetIdentifier
            });
        }
    }

    public canViewDetectionTraining(): boolean {
        return this.userProvider.hasDetectionBrainsCapability();
    }

    public isEvaluateBrain(): boolean {
        return this.datasetNavProvider.getActivePage() === DatasetNavLocation.EvaluateBrain;
    }

    public canEvaluateBrain(): boolean {
        if (!this.dataset) {
            return false;
        }

        return this.datasetProvider.canEvaluateBrain(this.dataset.id);
    }

    public goToEvaluateBrain(): void {
        if (this.canEvaluateBrain()) {
            this.appNavProvider.getNavController().setRoot(`EvaluateBrainPage`, {
                datasetIdentifier: this.datasetIdentifier
            });
        }
    }

    public isTestBrain(): boolean {
        return this.datasetNavProvider.getActivePage() === DatasetNavLocation.TestBrain;
    }

    public isLearningBrainTrained(): boolean {
        return (
            this.dataset !== undefined && this.dataset.brain !== undefined && this.dataset.brain.dateTrained !== null
        );
    }

    public isFrozenBrainTrained(): boolean {
        return this.dataset !== undefined && this.dataset.brain !== undefined && this.dataset.brain.dateFrozen !== null;
    }

    public isHiFiBrainTrained(): boolean {
        return (
            this.dataset !== undefined &&
            this.dataset.brain !== undefined &&
            this.dataset.brain.dateHiFiCreated !== null
        );
    }

    public canTestBrain(): boolean {
        if (!this.dataset) {
            return false;
        }

        return this.datasetProvider.canTestBrain(this.dataset);
    }

    public goToTestBrain(): void {
        if (this.canTestBrain()) {
            this.appNavProvider.getNavController().setRoot(`TestSetPage`, {
                datasetIdentifier: this.datasetIdentifier
            });
        }
    }

    public isDeployBrain(): boolean {
        return this.datasetNavProvider.getActivePage() === DatasetNavLocation.DeployBrain;
    }

    public canDeployBrain(): boolean {
        if (!this.dataset) {
            return false;
        }

        return this.datasetProvider.canDeployBrain(this.dataset);
    }

    public canSeeDeployBrainButton(): boolean {
        return this.userProvider.hasBrainPromotionCapability() || this.userProvider.hasInferenceAPICapability();
    }

    public goToDeployBrain(): void {
        if (this.canDeployBrain()) {
            this.appNavProvider.getNavController().setRoot(`DeployPage`, {
                datasetIdentifier: this.datasetIdentifier
            });
        }
    }

    public isOnExportWhiteLabelBrainPage(): boolean {
        return this.datasetNavProvider.getActivePage() === DatasetNavLocation.ExportWhiteLabelBrain;
    }

    public canExportWhiteLabelBrain(): boolean {
        if (this.isFireflyUser()) {
            return this.isFrozenBrainTrained();
        }

        return this.isLearningBrainTrained();
    }

    public isFireflyUser(): boolean {
        return this.userProvider.isFireflyUser();
    }

    public isWhiteLabelUser(): boolean {
        return this.userProvider.isWhiteLabelUser();
    }

    public isSonyUser(): boolean {
        return this.userProvider.isSonyUser();
    }

    public hasEnableWhitelabelDownloadCapability(): boolean {
        return this.userProvider.hasEnableWhitelabelDownloadCapability();
    }

    public goToExportWhiteLabelBrain(): void {
        if (this.canExportWhiteLabelBrain()) {
            this.appNavProvider.getNavController().setRoot(`ExportWhiteLabelBrainPage`, {
                datasetIdentifier: this.datasetIdentifier
            });
        }
    }

    public canExport(): boolean {
        return this.dataset ? !this.isSceneRecognitionDataset() : false;
    }

    public brainIsDirty(): boolean {
        if (this.dataset && this.dataset.brain) {
            return this.dataset.brain.dirty || this.dataset.isFrozenBrainOutdated();
        }

        return false;
    }

    public brainHasStaticClassifier(): boolean {
        if (this.dataset) {
            return this.isSceneRecognitionDataset() && this.dataset.hasFrozenBrain();
        }

        return false;
    }

    // Used for both LC and AR brains
    public learningBrainIsDirty(): boolean {
        if (this.dataset && this.dataset.brain) {
            return this.dataset.brain.dirty;
        }

        return false;
    }

    public frozenBrainIsDirty(): boolean {
        if (this.dataset) {
            return this.dataset.isFrozenBrainOutdated();
        }

        return false;
    }

    public hifiBrainIsDirty(): boolean {
        if (this.dataset) {
            return this.dataset.isHiFiBrainOutdated();
        }

        return false;
    }

    public isSceneRecognitionDataset(): boolean {
        return this.dataset !== undefined && this.dataset.isSceneRecognition();
    }

    public isAnomalyDataset(): boolean {
        return this.dataset !== undefined && this.dataset.isAnomalyRecognition();
    }

    public isDetectionDataset(): boolean {
        return this.dataset !== undefined && this.dataset.isObjectDetection();
    }

    public isTrainableDataset(): boolean {
        return this.isAnomalyDataset() || this.isSceneRecognitionDataset();
    }

    public goToMainDashboard(): void {
        this.appNavProvider.getNavController().setRoot('DashboardPage');
    }

    public toggleProjectList(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();

        this.datasetListVisible = false;
        this.projectListVisible = !this.projectListVisible;
        this.recalculateProjectListMenuStyles();

        // Must wrap in setTimeout to work
        setTimeout(() => {
            this.projectSearch.nativeElement.focus();

            if (this.projectList) {
                this.projectListHasScrolling =
                    this.projectList.nativeElement.scrollHeight > this.projectList.nativeElement.clientHeight;
            }
        }, 0);
    }

    public toggleDatasetList(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();

        this.projectListVisible = false;
        this.datasetListVisible = !this.datasetListVisible;
        this.recalculateDatasetListMenuStyles();

        // Must wrap in setTimeout to work
        setTimeout(() => {
            this.datasetSearch.nativeElement.focus();

            if (this.datasetList) {
                this.datasetListHasScrolling =
                    this.datasetList.nativeElement.scrollHeight > this.datasetList.nativeElement.clientHeight;
            }
        }, 0);
    }

    public recalculateProjectListMenuStyles() {
        if (!this.projectListVisible || !this.projectListButton || !this.projectListButton.nativeElement) {
            return;
        }

        const bounds = this.projectListButton.nativeElement.getBoundingClientRect() as ClientRect;
        this.projectListStyles = {
            // Ensure 4 px of vertical margins between the main button and the list.
            'top.px': bounds.bottom + 21
        };
    }

    public recalculateDatasetListMenuStyles() {
        if (!this.datasetListVisible || !this.datasetListButton || !this.datasetListButton.nativeElement) {
            return;
        }

        const bounds = this.datasetListButton.nativeElement.getBoundingClientRect() as ClientRect;
        this.datasetListStyles = {
            // Ensure 4 px of vertical margins between the main button and the list.
            'top.px': bounds.bottom + 21
        };
    }

    public projectIsSelected(projectID: string): boolean {
        return this.project !== undefined && projectID === this.project.id;
    }

    public datasetIsSelected(datasetID: string): boolean {
        return this.dataset !== undefined && datasetID === this.dataset.id;
    }

    public canCreateProject(): boolean {
        return this.userProvider.canManageProjects();
    }

    public canCreateDataset(): boolean {
        return this.userProvider.canManageDatasets();
    }

    public showCreateProjectModal(): void {
        const createProjectModal = this.modalProvider.createModal(
            'CreateProjectModal',
            {},
            { cssClass: 'modal-create-project' }
        );

        createProjectModal.onDidDismiss(data => {
            if (data && data.success && data.project) {
                this.createProjectCallback(data.project);
            }
        });

        createProjectModal.present();

        this.projectListVisible = false;
    }

    public createProjectCallback(project: Project) {
        if (project && project.datasetIdentifiers.length > 0) {
            this.appNavProvider.getNavController().setRoot(`DatasetPage`, {
                datasetIdentifier: project.datasetIdentifiers[0]
            });
        } else {
            this.appNavProvider.getNavController().setRoot(`ProjectPage`, {
                projectIdentifier: project.id
            });
        }
    }

    public navigateToCreateDatasetPage(): void {
        this.datasetListVisible = false;

        this.appNavProvider
            .getNavController()
            .setRoot(`CreateDatasetPage`, { projectIdentifier: this.projectIdentifier });
    }

    public navigateToProject(projectID: string): void {
        this.appNavProvider.getNavController().setRoot(`ProjectPage`, {
            projectIdentifier: projectID
        });

        this.projectListVisible = false;
    }

    public navigateToDataset(datasetID: string): void {
        this.appNavProvider.getNavController().setRoot(`DatasetPage`, {
            datasetIdentifier: datasetID
        });

        this.datasetListVisible = false;
    }

    public navigateToSettingsPage(): void {
        this.appNavProvider.getNavController().setRoot('AccountPage', {
            subPage: this.getSettingsSubpage(),
            previousProjectIdentifier: this.projectIdentifier,
            previousDatasetIdentifier: this.datasetIdentifier
        });
    }

    private getSettingsSubpage() {
        if (this.userProvider.canViewAPIKeysPage()) {
            return AccountSettingsViews.API_KEYS;
        }

        if (this.userProvider.isWhiteLabelUser() && this.userProvider.canManageExports()) {
            return AccountSettingsViews.EXPORTS;
        }

        if (this.userProvider.isTaggerUser()) {
            return AccountSettingsViews.ORGANIZATION;
        }

        if (this.userProvider.hasInferenceAPICapability()) {
            return AccountSettingsViews.API_ANALYTICS;
        }

        return AccountSettingsViews.NOTIFICATION_PREFERENCES;
    }

    public getUserInformation(): string {
        const userInfo = this.userProvider.getUserInformation();
        return userInfo && userInfo.name;
    }

    private getDatasets(): Dataset[] {
        // For tagger / annotator account we can get all the datasets assigned to the user
        if (this.userProvider.isTaggerUser()) {
            return this.datasetProvider.getDatasets();
        }

        if (this.projectIdentifier) {
            return this.datasetProvider.getDatasetsForProject(this.projectIdentifier);
        }

        return [];
    }

    public getProjectsDropdownString(): string {
        if (this.project) {
            return this.project.name;
        }

        return `${this.translate.instant('Select')}...`;
    }

    public getDatasetsDropdownString(): string {
        if (this.dataset) {
            return this.dataset.name;
        }

        return `${this.translate.instant('Select')}...`;
    }

    public canShowDatasetsDropdown(): boolean {
        return this.project || this.userProvider.isTaggerUser() ? true : false;
    }

    public isDatasetsDropdownDisabled(): boolean {
        if (this.userProvider.isTaggerUser()) {
            return false;
        }

        return !this.datasets.length;
    }

    public handleSyncNavigation(currentState: OutOfSyncState): void {
        switch (currentState) {
            case OutOfSyncState.AnomalyRecognizer:
            case OutOfSyncState.LearningClassifier:
            case OutOfSyncState.WhiteLabelLearningClassifier:
                this.showRetrainProgress();
                break;
            case OutOfSyncState.Detector:
                this.appNavProvider.getNavController().setRoot(`DetectionTrainingPage`, {
                    datasetIdentifier: this.datasetIdentifier
                });
                break;
            case OutOfSyncState.AnomalyHiFi:
                this.appNavProvider.getNavController().setRoot(`EvaluateBrainPage`, {
                    rebuildAnomalyHiFi: true,
                    datasetIdentifier: this.datasetIdentifier
                });
                break;
            case OutOfSyncState.StaticClassifier:
            case OutOfSyncState.WhiteLabelStaticClassifier:
                this.appNavProvider.getNavController().setRoot(`EvaluateBrainPage`, {
                    rebuildStaticClassifier: true,
                    datasetIdentifier: this.datasetIdentifier
                });
                break;
        }
    }

    public allowToDismissJob(job: JobResponse): boolean {
        return [JobState.DONE, JobState.CANCELED, JobState.ERROR].includes(job.state);
    }

    public async dismissJob(job: JobResponse): Promise<void> {
        await this.jobProvider.dismiss(job.jobIdentifier);
        await this.trackableJobProvider.restartJobPolling();
    }

    public async dismissJobOnClick(job: JobResponse, event: MouseEvent): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        await this.dismissJob(job);
    }

    public async goToDatasetJob(job: JobResponse): Promise<void> {
        if (this.allowToDismissJob(job)) {
            await this.dismissJob(job);
        }

        if (job.datasetIdentifier) {
            const dataset = this.datasetProvider.getDatasetById(job.datasetIdentifier);

            if (dataset) {
                if (job.type == JobType.Retrain) {
                    this.handleRetrainJob(job);
                } else if (job.type == JobType.Optimize) {
                    this.handleOptimizeJob(job);
                } else if (job.type == JobType.Freeze) {
                    this.handleFreezeJob(job);
                } else if (job.type == JobType.AnomalyHiFi) {
                    this.handleAnomalyHiFiJob(job);
                } else if (job.type == JobType.Train) {
                    this.handleDetectionTrainJob(job);
                } else if (job.type == JobType.BrainExport) {
                    this.handleBrainExportJob(job);
                }
            }
        }
    }

    // Subfunctions for goToDatasetJob()
    private handleRetrainJob(job: JobResponse) {
        if (this.jobProvider.isJobDone(job.state) && job.state != JobState.ERROR) {
            this.appNavProvider.getNavController().setRoot(`EvaluateBrainPage`, {
                datasetIdentifier: job.datasetIdentifier
            });
        } else {
            this.boostProvider.showRetrainProgress(job.datasetIdentifier);
        }
    }

    private handleOptimizeJob(job: JobResponse) {
        if (job.state === JobState.DONE) {
            this.appNavProvider.getNavController().setRoot(`EvaluateBrainPage`, {
                datasetIdentifier: job.datasetIdentifier,
                navigateToWorkingBrain: true
            });
        } else {
            this.appNavProvider.getNavController().setRoot(`EvaluateBrainPage`, {
                datasetIdentifier: job.datasetIdentifier,
                optimizeWorkingBrain: true,
                hasError: job.state === JobState.ERROR
            });
        }
    }

    private async handleFreezeJob(job: JobResponse): Promise<void> {
        this.appNavProvider.getNavController().setRoot(`EvaluateBrainPage`, {
            datasetIdentifier: job.datasetIdentifier,
            navigateToStaticTab: true,
            hasError: job.state === JobState.ERROR
        });
    }

    // Dismiss the first anomaly hi-fi build job for an AR brain is similar
    // to dismissing the first freeze job for an SC brain.
    private async handleAnomalyHiFiJob(job: JobResponse): Promise<void> {
        this.appNavProvider.getNavController().setRoot(`EvaluateBrainPage`, {
            datasetIdentifier: job.datasetIdentifier,
            navigateToHiFiTab: true,
            hasError: job.state === JobState.ERROR
        });
    }

    private handleDetectionTrainJob(job: JobResponse) {
        if (this.jobProvider.isJobDone(job.state) && job.state === JobState.DONE) {
            this.appNavProvider.getNavController().setRoot('EvaluateBrainPage', {
                datasetIdentifier: job.datasetIdentifier
            });
        } else {
            this.appNavProvider.getNavController().setRoot(`DetectionTrainingPage`, {
                datasetIdentifier: job.datasetIdentifier,
                hasError: job.state === JobState.ERROR
            });
        }
    }

    private handleBrainExportJob(job: JobResponse) {
        this.appNavProvider.getNavController().setRoot(`ExportWhiteLabelBrainPage`, {
            datasetIdentifier: job.datasetIdentifier,
            hasError: job.state === JobState.ERROR,
            downloadJobIdentifier: job.state === JobState.DONE ? job.jobIdentifier : undefined
        });
    }

    // End of subfunctions for goToDatasetJob()

    private showRetrainProgress() {
        if (this.datasetIdentifier) {
            this.boostProvider.showRetrainProgress(this.datasetIdentifier, true);
        }
    }

    public shouldShowLearningClassifierSyncWarning(): boolean {
        return this.isSceneRecognitionDataset() && this.learningBrainIsDirty();
    }

    public shouldShowStaticClassifierSyncWarning(): boolean {
        if (!this.dataset || !this.isSceneRecognitionDataset()) {
            return false;
        }

        // For a WhiteLabel dataset's enableStaticClassifier property will be null,
        // so we need to account for that as well.
        if (!this.dataset.enableStaticClassifier && !this.isWhiteLabelUser()) {
            return false;
        }

        return this.frozenBrainIsDirty();
    }

    public shouldShowAnomalyRecognizerSyncWarning(): boolean {
        return this.isAnomalyDataset() && this.learningBrainIsDirty();
    }

    public shouldShowAnomalyHiFiSyncWarning(): boolean {
        if (!this.dataset || !this.isAnomalyDataset()) {
            return false;
        }

        if (!this.dataset.enableAnomalyHiFi) {
            return false;
        }

        return this.hifiBrainIsDirty();
    }

    public shouldShowDetectionSyncWarning(): boolean {
        return this.isDetectionDataset() && this.learningBrainIsDirty();
    }

    // Due to the way out of sync warnings are instantiated, we need to check each case
    // to see if we're displaying any.
    public shouldUseCollapsedSyncWarningStyles(): boolean {
        return (
            !this.isExpanded &&
            (this.shouldShowLearningClassifierSyncWarning() ||
                this.shouldShowStaticClassifierSyncWarning() ||
                this.shouldShowAnomalyRecognizerSyncWarning() ||
                this.shouldShowAnomalyHiFiSyncWarning())
        );
    }

    public shouldShowNavBreadcrumbsBorder(): boolean {
        const showBorderWhenExpanded = this.isExpanded && (this.project !== undefined || this.dataset !== undefined);

        return showBorderWhenExpanded || this.shouldUseCollapsedSyncWarningStyles();
    }

    /**
     * Filter and sort data from polled jobs for display in the sidebar
     */
    private getSidebarJobs(jobs: JobResponse[]): JobResponse[] {
        return jobs
            .filter(job => job.state != JobState.CANCELED)
            .sort((job1, job2) => {
                return job1.dateProcessingStartedSeconds - job2.dateProcessingStartedSeconds;
            });
    }

    public shouldSeeWhiteLabelCopy(): boolean {
        return this.userProvider.shouldSeeWhiteLabelCopy();
    }

    public getSidebarJobTooltipText(job: JobResponse): string {
        return this.sidebarJobProvider.getTooltip(
            job,
            this.userProvider.shouldSeeWhiteLabelCopy(),
            this.shouldShowIndeterminateLoading(job)
        );
    }

    public getSidebarJobActionText(job: JobResponse): string {
        return this.sidebarJobProvider.getAction(job);
    }

    public getJobProgressData(job: JobResponse) {
        if (job) {
            const percentage = job.state == JobState.DONE ? 100 : this.jobProvider.getProgressPercentage(job);

            return {
                value: `${percentage.toString()}%`,
                name: 'JobProgress',
                color: '#30A8BC'
            };
        } else {
            return {
                value: `0%`,
                name: 'JobProgress',
                color: '#30A8BC'
            };
        }
    }

    public shouldShowIndeterminateLoading(job: JobResponse): boolean {
        switch (job.state) {
            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
            case JobState.WAITING:
                return true;

            case JobState.DONE:
            case JobState.ERROR:
            case JobState.CANCELED:
                return false;

            default:
                return this.trackableJobProvider.isProgressIndeterminate(job.type);
        }
    }

    public getJobsStyleHeight(): string {
        const numberOfJobs = this.sidebarJobs ? this.sidebarJobs.length : 0;
        const numberOfJobsToDisplay = Math.min(numberOfJobs, MAX_JOBS_TO_DISPLAY);
        const height = 48 * numberOfJobsToDisplay;
        return `${height}px`;
    }

    /**
     * Return true if there is a new job added to the newJobs array as compared to
     * the oldJobs array
     */
    private hasNewJob(oldJobs: JobResponse[], newJobs: JobResponse[]): boolean {
        const latestOldJob = oldJobs && oldJobs.length ? oldJobs[oldJobs.length - 1] : null;
        const latestNewJob = newJobs && newJobs.length ? newJobs[newJobs.length - 1] : null;

        const latestOldJobStart = latestOldJob ? latestOldJob.dateProcessingStartedSeconds || 0 : 0;
        const latestNewJobStart = latestNewJob ? latestNewJob.dateProcessingStartedSeconds || 0 : 0;

        return latestNewJobStart > latestOldJobStart;
    }

    public filterProjects(): void {
        this.filteredProjects = this.projects.filter(obj =>
            obj.name.toLowerCase().includes(this.projectSearchQuery.toLowerCase())
        );
    }

    public filterDatasets(): void {
        this.filteredDatasets = this.datasets.filter(obj =>
            obj.name.toLowerCase().includes(this.datasetSearchQuery.toLowerCase())
        );
    }

    public clearProjectsSearch(event: Event): void {
        this.projectSearchQuery = '';
        this.filteredProjects = this.projects;

        event.stopPropagation();
    }

    public clearDatasetsSearch(event: Event): void {
        this.datasetSearchQuery = '';
        this.filteredDatasets = this.datasets;

        event.stopPropagation();
    }

    public getTrainAnnotateTabText(): string {
        return this.isTrainableDataset() ? this.translate.instant('Train') : this.translate.instant('Annotate');
    }
}



// WEBPACK FOOTER //
// ./src/components/core/nav-sidebar/nav-sidebar.ts